import { useMemo } from 'react';
import * as THREE from 'three';
import { Html } from '@react-three/drei';
import { useSketchStore } from '../../state/useSketchStore';
import { SKETCH_COLORS, AXIS_COLORS } from '../../themes/colors';
import { emitSketchDown, emitSketchMove } from '../../tools/sketch/sketchToolBus';

/**
 * Sketch annotation layer — Dimension and Text annotations plus the custom
 * drawing-axes indicator. Annotations render thin lines + DOM labels, and
 * carry a small pick dot that routes annotation hits onto the tool bus (the
 * Eraser removes them).
 */

const asVec = (p: [number, number, number]): THREE.Vector3 => new THREE.Vector3(...p);

function SolidLine({ a, b, color, dashed = false }: { a: THREE.Vector3; b: THREE.Vector3; color: string; dashed?: boolean }) {
    const line = useMemo(() => {
        const geometry = new THREE.BufferGeometry().setFromPoints([a, b]);
        const material = dashed
            ? new THREE.LineDashedMaterial({ color: new THREE.Color(color), dashSize: 0.18, gapSize: 0.12, depthWrite: false, transparent: true, opacity: 0.9 })
            : new THREE.LineBasicMaterial({ color: new THREE.Color(color), depthWrite: false, transparent: true, opacity: 0.9 });
        const obj = new THREE.Line(geometry, material);
        if (dashed) obj.computeLineDistances();
        obj.raycast = () => null;
        return obj;
    }, [a, b, color, dashed]);
    return <primitive object={line} />;
}

/** Small invisible sphere that routes annotation picks through the bus. */
function AnnotationPickDot({ annotationId, point }: { annotationId: string; point: THREE.Vector3 }) {
    return (
        <mesh
            position={point}
            onPointerDown={(e) => {
                if (e.button !== 0) return;
                e.stopPropagation();
                emitSketchDown({
                    kind: 'annotation',
                    annotationId,
                    point: e.point.clone(),
                    shiftKey: e.nativeEvent.shiftKey,
                    ctrlKey: e.nativeEvent.ctrlKey,
                    altKey: e.nativeEvent.altKey,
                    button: e.button,
                    pointerPx: { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY },
                });
            }}
            onPointerMove={(e) => {
                e.stopPropagation();
                emitSketchMove({
                    kind: 'annotation',
                    annotationId,
                    point: e.point.clone(),
                    shiftKey: e.nativeEvent.shiftKey,
                    ctrlKey: e.nativeEvent.ctrlKey,
                    altKey: e.nativeEvent.altKey,
                    button: -1,
                    pointerPx: { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY },
                });
            }}
        >
            <sphereGeometry args={[0.12, 8, 8]} />
            <meshBasicMaterial color={SKETCH_COLORS.guide} transparent opacity={0.6} />
        </mesh>
    );
}

function AnnotationText({ position, text, color }: { position: THREE.Vector3; text: string; color: string }) {
    return (
        <Html position={position} center zIndexRange={[8, 0]} style={{ pointerEvents: 'none' }}>
            <span
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color,
                    background: 'rgba(255,255,255,0.88)',
                    border: `1px solid ${color}44`,
                    borderRadius: 4,
                    padding: '1px 5px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'inherit',
                }}
            >
                {text}
            </span>
        </Html>
    );
}

function DimensionEntity({ id, a, b, offset, text }: {
    id: string;
    a: THREE.Vector3;
    b: THREE.Vector3;
    offset: THREE.Vector3;
    text: string;
}) {
    const color = SKETCH_COLORS.guide;
    // Dim line: parallel to a→b through the picked offset point; witness
    // lines connect the measured points to their foot points on it.
    const dir = b.clone().sub(a);
    if (dir.lengthSq() < 1e-12) return null;
    dir.normalize();
    const tA = offset.clone().sub(a).dot(dir);
    const tB = offset.clone().sub(b).dot(dir);
    const footA = a.clone().addScaledVector(dir, tA);
    const footB = b.clone().addScaledVector(dir, tB);
    return (
        <group>
            <SolidLine a={a} b={footA} color={color} dashed />
            <SolidLine a={b} b={footB} color={color} dashed />
            <SolidLine a={footA} b={footB} color={color} />
            <AnnotationPickDot annotationId={id} point={offset} />
            <AnnotationText position={offset} text={text} color={color} />
        </group>
    );
}

/** The custom drawing axes (Axes tool): red X / green Y / blue Z sticks. */
function AxesIndicator() {
    const drawingAxes = useSketchStore((s) => s.drawingAxes);
    if (!drawingAxes) return null;
    const origin = asVec(drawingAxes.origin);
    const xAxis = asVec(drawingAxes.xAxis).normalize();
    const yAxis = asVec(drawingAxes.yAxis).normalize();
    const zAxis = new THREE.Vector3().crossVectors(xAxis, yAxis).normalize();
    const size = 8;
    return (
        <group>
            <SolidLine a={origin} b={origin.clone().addScaledVector(xAxis, size)} color={AXIS_COLORS.x} />
            <SolidLine a={origin} b={origin.clone().addScaledVector(yAxis, size)} color={AXIS_COLORS.y} />
            <SolidLine a={origin} b={origin.clone().addScaledVector(zAxis, size)} color={AXIS_COLORS.z} />
        </group>
    );
}

export function SketchAnnotationLayer() {
    const dimensions = useSketchStore((s) => s.dimensions);
    const labels = useSketchStore((s) => s.labels);

    return (
        <group>
            <AxesIndicator />
            {Object.values(dimensions).map((dim) => (
                <DimensionEntity
                    key={dim.id}
                    id={dim.id}
                    a={asVec(dim.a)}
                    b={asVec(dim.b)}
                    offset={asVec(dim.offset)}
                    text={dim.text ?? dim.value.toFixed(2)}
                />
            ))}
            {Object.values(labels).map((label) => {
                const anchor = asVec(label.anchor);
                const leader = asVec(label.leader);
                return (
                    <group key={label.id}>
                        <SolidLine a={anchor} b={leader} color={SKETCH_COLORS.guide} />
                        <AnnotationPickDot annotationId={label.id} point={anchor} />
                        <AnnotationText position={leader} text={label.text} color={SKETCH_COLORS.guide} />
                    </group>
                );
            })}
        </group>
    );
}
